import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { superAdminApi } from "../../../../services/superAdmin/api";

const PAYMENT_STATUS_OPTIONS = ["PAID", "PENDING", "FAILED"];
const PAYOUT_STATUS_OPTIONS = ["PENDING", "PAID", "FAILED"];
const INSTITUTE_TYPE_OPTIONS = [
  { label: "Institute", value: "institute" },
  { label: "Library", value: "library" },
];

const buildInitialState = (admin) => ({
  ownerName: admin?.owner?.name || admin?.fullName || "",
  ownerEmail: admin?.owner?.email || admin?.email || "",
  ownerMobile: admin?.owner?.mobile || admin?.phoneNumber || "",
  instituteType: admin?.institute?.role || admin?.institute?.type || admin?.type || "institute",
  instituteName: admin?.institute?.name || admin?.assignedInstituteName || "",
  instituteEmail: admin?.institute?.email || "",
  instituteMobile: admin?.institute?.mobile || "",
  instituteLogo: admin?.institute?.logo || "",
  addressLine: admin?.institute?.address?.addressLine || "",
  city: admin?.institute?.address?.city || "",
  state: admin?.institute?.address?.state || "",
  pincode: admin?.institute?.address?.pincode || "",
  credentialEmail: admin?.institute?.credentialEmail || "",
  password: "",
  isSubscribed: Boolean(admin?.institute?.isSubscribed ?? true),
  studentCount: String(admin?.institute?.studentCount ?? 0),
  planId: admin?.institute?.plan?.planId || "",
  paymentAmount: String(admin?.institute?.payment?.amount ?? ""),
  paymentStatus: admin?.institute?.payment?.paymentStatus || "PAID",
  paymentId: admin?.institute?.payment?.paymentId || "",
  transactionId: admin?.institute?.payment?.transactionId || "",
  paymentPaidAt: admin?.institute?.payment?.paidAt?.slice?.(0, 10) || "",
  earningPercentage: String(admin?.institute?.earning?.percentage ?? ""),
  payoutStatus: admin?.institute?.earning?.payoutStatus || "PENDING",
  earningPaidAt: admin?.institute?.earning?.paidAt?.slice?.(0, 10) || "",
});

const AdminFormDrawer = ({ admin, isLoading, isOpen, onClose, onSubmit }) => {
  const [values, setValues] = useState(buildInitialState(admin));
  const [errors, setErrors] = useState({});
  const plansQuery = useQuery({
    queryKey: ["super-admin", "institute-plans"],
    queryFn: async () => {
      const response = await superAdminApi.getInstitutePlans();
      return response.result.items;
    },
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setValues(buildInitialState(admin));
    setErrors({});
  }, [admin, isOpen]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;

    setValues((currentValues) => ({
      ...currentValues,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
    }));
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const validateMobile = (mobile) => {
    const normalizedMobile = String(mobile).replace(/\D/g, "");
    return normalizedMobile.length >= 10 && normalizedMobile.length <= 15;
  };

  const validateForm = () => {
    const nextErrors = {};
    const rawPassword = String(values.password || "");

    if (!values.ownerName.trim()) {
      nextErrors.ownerName = "Owner name is required.";
    }

    if (!validateEmail(values.ownerEmail)) {
      nextErrors.ownerEmail = "Enter a valid owner email.";
    }

    if (!validateMobile(values.ownerMobile)) {
      nextErrors.ownerMobile = "Owner mobile must contain 10 to 15 digits.";
    }

    if (!values.instituteName.trim()) {
      nextErrors.instituteName = "Institute name is required.";
    }

    if (!validateEmail(values.instituteEmail)) {
      nextErrors.instituteEmail = "Enter a valid institute email.";
    }

    if (!validateMobile(values.instituteMobile)) {
      nextErrors.instituteMobile = "Institute mobile must contain 10 to 15 digits.";
    }

    if (!values.addressLine.trim()) {
      nextErrors.addressLine = "Address line is required.";
    }

    if (!values.city.trim()) {
      nextErrors.city = "City is required.";
    }

    if (!values.state.trim()) {
      nextErrors.state = "State is required.";
    }

    if (!String(values.pincode).trim()) {
      nextErrors.pincode = "Pincode is required.";
    }

    if (!validateEmail(values.credentialEmail)) {
      nextErrors.credentialEmail = "Enter a valid credential email.";
    }

    if (!admin && !rawPassword) {
      nextErrors.password = "Password is required.";
    }

    if (rawPassword && rawPassword.length < 8) {
      nextErrors.password = "Password must contain at least 8 characters.";
    }

    if (!values.planId.trim()) {
      nextErrors.planId = "Plan ID is required.";
    }

    if (values.paymentAmount === "" || Number(values.paymentAmount) < 0) {
      nextErrors.paymentAmount = "Enter a valid payment amount.";
    }

    if (values.earningPercentage === "" || Number(values.earningPercentage) < 0) {
      nextErrors.earningPercentage = "Enter a valid earning percentage.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const normalizeMobile = (mobile) => String(mobile).replace(/\D/g, "");
  const emptyDateToNull = (value) => (value ? value : null);
  const planOptions = plansQuery.data || [];

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      owner: {
        name: values.ownerName.trim(),
        email: values.ownerEmail.trim().toLowerCase(),
        mobile: normalizeMobile(values.ownerMobile),
      },
      institute: {
        role: values.instituteType,
        name: values.instituteName.trim(),
        email: values.instituteEmail.trim().toLowerCase(),
        mobile: normalizeMobile(values.instituteMobile),
        logo: values.instituteLogo.trim(),
        address: {
          addressLine: values.addressLine.trim(),
          city: values.city.trim(),
          state: values.state.trim(),
          pincode: String(values.pincode).trim(),
        },
        credentialEmail: values.credentialEmail.trim().toLowerCase(),
        ...(values.password ? { password: values.password } : {}),
        isSubscribed: values.isSubscribed,
        studentCount: Number(values.studentCount) || 0,
        plan: {
          planId: values.planId.trim(),
        },
        payment: {
          amount: Number(values.paymentAmount) || 0,
          paymentStatus: values.paymentStatus,
          paymentId: values.paymentId.trim(),
          transactionId: values.transactionId.trim(),
          paidAt: emptyDateToNull(values.paymentPaidAt),
        },
        earning: {
          percentage: Number(values.earningPercentage) || 0,
          payoutStatus: values.payoutStatus,
          paidAt: emptyDateToNull(values.earningPaidAt),
        },
      },
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      // Parent mutations already surface the validation/API message via toast.
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={isLoading ? () => {} : onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton disabled={isLoading} />
        <DrawerHeader>{admin ? "Edit Institute Admin" : "Create Institute Admin"}</DrawerHeader>

        <DrawerBody>
          <div className="space-y-5">
            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <Text className="text-sm font-semibold text-gray-800">Owner</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} className="mt-4">
                <FormControl isInvalid={Boolean(errors.ownerName)}>
                  <FormLabel>Owner Name</FormLabel>
                  <Input name="ownerName" placeholder="Praful Sharma" value={values.ownerName} onChange={handleChange} />
                  <FormErrorMessage>{errors.ownerName}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(errors.ownerEmail)}>
                  <FormLabel>Owner Email</FormLabel>
                  <Input name="ownerEmail" placeholder="praful@gmail.com" value={values.ownerEmail} onChange={handleChange} />
                  <FormErrorMessage>{errors.ownerEmail}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(errors.ownerMobile)}>
                  <FormLabel>Owner Mobile</FormLabel>
                  <Input name="ownerMobile" placeholder="9876543211" value={values.ownerMobile} onChange={handleChange} />
                  <FormErrorMessage>{errors.ownerMobile}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <Text className="text-sm font-semibold text-gray-800">Institute</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} className="mt-4">
                <FormControl>
                  <FormLabel>Type</FormLabel>
                  <Select name="instituteType" value={values.instituteType} onChange={handleChange}>
                    {INSTITUTE_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isInvalid={Boolean(errors.instituteName)}>
                  <FormLabel>Institute Name</FormLabel>
                  <Input name="instituteName" placeholder="ABC Institute" value={values.instituteName} onChange={handleChange} />
                  <FormErrorMessage>{errors.instituteName}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(errors.instituteEmail)}>
                  <FormLabel>Institute Email</FormLabel>
                  <Input name="instituteEmail" placeholder="abc@gmail.com" value={values.instituteEmail} onChange={handleChange} />
                  <FormErrorMessage>{errors.instituteEmail}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(errors.instituteMobile)}>
                  <FormLabel>Institute Mobile</FormLabel>
                  <Input name="instituteMobile" placeholder="9876543211" value={values.instituteMobile} onChange={handleChange} />
                  <FormErrorMessage>{errors.instituteMobile}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Logo URL</FormLabel>
                  <Input name="instituteLogo" placeholder="https://example.com/logo.png" value={values.instituteLogo} onChange={handleChange} />
                </FormControl>
              </SimpleGrid>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <Text className="text-sm font-semibold text-gray-800">Address</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} className="mt-4">
                <FormControl isInvalid={Boolean(errors.addressLine)}>
                  <FormLabel>Address Line</FormLabel>
                  <Input name="addressLine" placeholder="Main Road" value={values.addressLine} onChange={handleChange} />
                  <FormErrorMessage>{errors.addressLine}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(errors.city)}>
                  <FormLabel>City</FormLabel>
                  <Input name="city" placeholder="Bhopal" value={values.city} onChange={handleChange} />
                  <FormErrorMessage>{errors.city}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(errors.state)}>
                  <FormLabel>State</FormLabel>
                  <Input name="state" placeholder="Madhya Pradesh" value={values.state} onChange={handleChange} />
                  <FormErrorMessage>{errors.state}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(errors.pincode)}>
                  <FormLabel>Pincode</FormLabel>
                  <Input name="pincode" placeholder="462001" value={values.pincode} onChange={handleChange} />
                  <FormErrorMessage>{errors.pincode}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <Text className="text-sm font-semibold text-gray-800">Credentials & Plan</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} className="mt-4">
                <FormControl isInvalid={Boolean(errors.credentialEmail)}>
                  <FormLabel>Credential Email</FormLabel>
                  <Input name="credentialEmail" placeholder="test@gmail.com" value={values.credentialEmail} onChange={handleChange} />
                  <FormErrorMessage>{errors.credentialEmail}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(errors.password)}>
                  <FormLabel>Password</FormLabel>
                  <Input
                    name="password"
                    type="password"
                    placeholder={admin ? "Leave blank to keep current password" : "12345678"}
                    value={values.password}
                    onChange={handleChange}
                  />
                  <FormErrorMessage>{errors.password}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Subscribed</FormLabel>
                  <Checkbox name="isSubscribed" isChecked={values.isSubscribed} onChange={handleChange}>
                    Active subscription
                  </Checkbox>
                </FormControl>

                <FormControl>
                  <FormLabel>Student Count</FormLabel>
                  <Input name="studentCount" type="number" min="0" placeholder="0" value={values.studentCount} onChange={handleChange} />
                </FormControl>

                <FormControl isInvalid={Boolean(errors.planId)}>
                  <FormLabel>Plan</FormLabel>
                  <Select
                    name="planId"
                    value={values.planId}
                    onChange={handleChange}
                    placeholder={
                      plansQuery.isLoading
                        ? "Loading plans..."
                        : plansQuery.isError
                          ? "Unable to load plans"
                          : "Select plan"
                    }
                    isDisabled={plansQuery.isLoading}
                  >
                    {planOptions.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                        {plan.amount !== "" ? ` - Rs. ${plan.amount}` : ""}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.planId}</FormErrorMessage>
                  {plansQuery.isError && (
                    <Text className="mt-1 text-xs text-red-500">
                      Plans load nahi ho paye. Drawer reopen karke retry karein.
                    </Text>
                  )}
                  {!plansQuery.isLoading && !plansQuery.isError && planOptions.length === 0 && (
                    <Text className="mt-1 text-xs text-gray-500">
                      No plans available.
                    </Text>
                  )}
                </FormControl>
              </SimpleGrid>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <Text className="text-sm font-semibold text-gray-800">Payment</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} className="mt-4">
                <FormControl isInvalid={Boolean(errors.paymentAmount)}>
                  <FormLabel>Amount</FormLabel>
                  <Input name="paymentAmount" type="number" min="0" placeholder="499" value={values.paymentAmount} onChange={handleChange} />
                  <FormErrorMessage>{errors.paymentAmount}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Payment Status</FormLabel>
                  <Select name="paymentStatus" value={values.paymentStatus} onChange={handleChange}>
                    {PAYMENT_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Payment ID</FormLabel>
                  <Input name="paymentId" placeholder="pay_xxxxx" value={values.paymentId} onChange={handleChange} />
                </FormControl>

                <FormControl>
                  <FormLabel>Transaction ID</FormLabel>
                  <Input name="transactionId" placeholder="txn_xxxxx" value={values.transactionId} onChange={handleChange} />
                </FormControl>

                <FormControl>
                  <FormLabel>Paid At</FormLabel>
                  <Input name="paymentPaidAt" type="date" value={values.paymentPaidAt} onChange={handleChange} />
                </FormControl>
              </SimpleGrid>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <Text className="text-sm font-semibold text-gray-800">Earning</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} className="mt-4">
                <FormControl isInvalid={Boolean(errors.earningPercentage)}>
                  <FormLabel>Percentage</FormLabel>
                  <Input name="earningPercentage" type="number" min="0" placeholder="40" value={values.earningPercentage} onChange={handleChange} />
                  <FormErrorMessage>{errors.earningPercentage}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Payout Status</FormLabel>
                  <Select name="payoutStatus" value={values.payoutStatus} onChange={handleChange}>
                    {PAYOUT_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Paid At</FormLabel>
                  <Input name="earningPaidAt" type="date" value={values.earningPaidAt || ""} onChange={handleChange} />
                </FormControl>
              </SimpleGrid>
            </div>
          </div>
        </DrawerBody>

        <DrawerFooter>
          <Button mr={3} onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button colorScheme="purple" onClick={handleSubmit} isLoading={isLoading}>
            {admin ? "Save Changes" : "Create Admin"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default AdminFormDrawer;
